// src/services/auth.js
import axios from "axios";
import { notifySessionExpired } from "./session";

// === Axios 인스턴스(export) ===
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE || "http://localhost:8000",
  withCredentials: false,
});

// 요청 시 토큰 자동 첨부
api.interceptors.request.use((config) => {
  // Prefer the real session token so TTL (1min) applies; fallback to fixed token when absent
  config.headers = config.headers || {};
  const tk = getToken();
  if (tk) {
    config.headers.Authorization = `Bearer ${tk}`;
  } else {
    // no token: ensure header is not set
    delete config.headers.Authorization;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const isLoginCall = url.includes("/auth/login");
    if ([401, 403].includes(status) && !isLoginCall) {
      // Never show raw backend messages like "Invalid token"
      notifySessionExpired("다시 로그인하세요.");
    }
    return Promise.reject(error);
  }
);

// ===== 세션 유틸 =====
export function getToken() {
  return localStorage.getItem("token");
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function setSession(token, user) {
  if (token) {
    localStorage.setItem("token", token);
    // Use real token for axios default
    api.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem("token");
    delete api.defaults.headers.Authorization;
  }
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
  }
}

// ===== Auth API =====

// 로그인
export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  const { token, user, requiresPasswordChange } = data; // { token, user, requiresPasswordChange }
  setSession(token, user);
  if (requiresPasswordChange) {
    localStorage.setItem("requiresPasswordChange", "true");
  } else {
    localStorage.removeItem("requiresPasswordChange");
  }
  return data;
}

// 로그아웃
export function logout() {
  setSession(null, null);
}

// 회원가입
export async function signup({ name, email, password, password2 }) {
  if (!name || !email || !password || !password2) {
    throw new Error("필수 입력값이 없습니다.");
  }
  if (password !== password2) {
    throw new Error("비밀번호가 일치하지 않습니다.");
  }
  const { data } = await api.post("/auth/signup", {
    name,
    email,
    password,
  });
  if (data?.token) setSession(data.token, data.user); // 백엔드가 토큰 주면 즉시 세션 세팅
  return data;
}

export async function checkEmailAvailability(email) {
  if (!email) throw new Error("이메일을 입력하세요.");
  const { data } = await api.get("/auth/email/check", { params: { email } });
  return data;
}

export async function sendEmailVerificationCode(email) {
  if (!email) throw new Error("이메일을 입력하세요.");
  const { data } = await api.post("/auth/email-otp/request", { email });
  return data;
}

export async function verifyEmailVerificationCode({ email, code, otpToken }) {
  if (!email || !code || !otpToken) throw new Error("이메일 + 코드 + 토큰이 필요합니다.");
  const { data } = await api.post("/auth/email-otp/verify", { email, code, otpToken });
  return data;
}

// 비밀번호 재설정 코드 요청
export async function requestResetCode(email) {
  if (!email) throw new Error("이메일을 입력하세요.");
  const { data } = await api.post("/auth/reset/request", { email });
  return data;
}

// 비밀번호 재설정 확정
export async function resetPassword({
  email,
  code,
  newPassword,
  newPassword2,
}) {
  if (!email || !code || !newPassword || !newPassword2) {
    throw new Error("필수 입력값이 없습니다.");
  }
  if (newPassword !== newPassword2) {
    throw new Error("새 비밀번호가 일치하지 않습니다.");
  }
  const { data } = await api.post("/auth/reset/confirm", {
    email,
    code,
    password: newPassword,
  });
  if (data?.token) setSession(data.token, data.user); // 선택
  return data;
}

// 임시 비밀번호 발급 요청
export async function requestTempPassword(email) {
  if (!email) throw new Error("이메일을 입력하세요.");
  const { data } = await api.post("/auth/temp-password/request", { email });
  return data;
}

// 비밀번호 변경 (로그인 사용자)
export async function changePassword({ currentPassword, newPassword, newPassword2 }) {
  if (!newPassword || !newPassword2) throw new Error("필수 입력값이 없습니다.");
  if (newPassword !== newPassword2) throw new Error("새 비밀번호가 일치하지 않습니다.");
  const payload = { newPassword };
  if (currentPassword) payload.currentPassword = currentPassword;
  const { data } = await api.post("/auth/change-password/me", payload);
  if (data?.ok) {
    localStorage.removeItem("requiresPasswordChange");
  }
  return data;
}

// 개발자 모드 로그인
export async function devLogin() {
  const { data } = await api.post("/auth/dev-login");
  const { token, user } = data;
  setSession(token, user);
  return data;
}
